import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import { getDocuments, ApiError, type ActorDocuments, type DocumentContract } from '../lib/api';
import './Career.css';

export interface CareerDocumentsProps {
  tenantId: string;
  onViewSource: (sourceId: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const CONTRACT_KIND_LABEL: Record<string, string> = {
  deal_memo: 'Deal memo',
  engagement: 'Engagement',
  nda: 'NDA',
  amendment: 'Amendment',
  release: 'Release',
  other: 'Contract',
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  under_review: 'Under review',
  signed: 'Signed',
  countersigned: 'Countersigned',
  declined: 'Declined',
};

const UNION_STATUS_LABEL: Record<string, string> = {
  non_union: 'Non-union',
  eligible: 'Eligible',
  apprentice: 'Apprentice',
  member: 'Member · in good standing',
  financial_core: 'Financial core',
  other: 'Member',
};

// CAREER / DOCUMENTS — Contracts (status badges + optional original), the
// Tax-and-filings summary, and the Union card. A read surface (the only write
// on this backend is the profile upsert, owned by onboarding).
export function CareerDocuments({ tenantId, onViewSource }: CareerDocumentsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<ActorDocuments | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getDocuments(tenantId);
        if (cancelled) return;
        setData(d);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Documents" line="One moment…" />;
  }
  if (state === 'error' || !data) {
    return (
      <PlaceholderScreen eyebrow="Documents" line="Couldn't load your documents." soft={errorMessage ?? undefined} />
    );
  }

  const { contracts, taxSummary, union } = data;
  const hasUnion = Boolean(union.name || union.status || union.memberId);

  return (
    <div class="career">
      <div class="career__eyebrow">Documents</div>

      <div class="career__grp">Contracts</div>
      {contracts.length === 0 ? (
        <div class="career__note" data-testid="contracts-empty">
          No contracts yet. Deal memos and releases land here once they&rsquo;re in.
        </div>
      ) : (
        contracts.map((c) => <ContractCard key={c.id} contract={c} onViewSource={onViewSource} />)
      )}

      <div class="career__grp">Tax and filings</div>
      <div class="career__card" style="cursor:default" data-testid="tax-summary">
        <div class="career__card-top">
          <span class="career__card-nm">Income summary · {taxSummary.year}</span>
        </div>
        <div class="career__card-sub">
          {taxSummary.creditCount} deductible {taxSummary.creditCount === 1 ? 'entry' : 'entries'} ·{' '}
          {taxSummary.expenseCount} total logged
        </div>
      </div>

      <div class="career__grp">Union</div>
      {hasUnion ? (
        <>
          <div class="career__kv" data-testid="union-row">
            <span class="career__kv-k">{union.name ?? 'Union'}</span>
            <span class="career__kv-v">
              {union.status ? (UNION_STATUS_LABEL[union.status] ?? union.status) : 'Status not set'}
            </span>
          </div>
          {union.memberId && (
            <div class="career__kv">
              <span class="career__kv-k">Member ID</span>
              <span class="career__kv-v">{union.memberId}</span>
            </div>
          )}
        </>
      ) : (
        <div class="career__note" data-testid="union-empty">
          No union set yet. You can add your membership from your profile.
        </div>
      )}
    </div>
  );
}

function ContractCard({
  contract,
  onViewSource,
}: {
  contract: DocumentContract;
  onViewSource: (sourceId: string) => void;
}) {
  const kindLabel = (contract.kind && CONTRACT_KIND_LABEL[contract.kind]) ?? 'Contract';
  const status = contract.status ?? 'received';
  const statusLabel = CONTRACT_STATUS_LABEL[status] ?? status;

  return (
    <div class="career__card" style="cursor:default" data-testid="contract-card">
      <div class="career__card-top">
        <span class="career__card-nm">{kindLabel}</span>
        <span class={`career-badge career-badge--${status}`}>{statusLabel}</span>
      </div>
      {contract.rate && <div class="career__card-sub">{contract.rate}</div>}
      {contract.keyDates && <div class="career__card-sub">{contract.keyDates}</div>}
      {contract.respondBy && <div class="career__card-sub">Response due {contract.respondBy}</div>}
      {contract.sourceId && (
        <div class="career__src-chip">
          <button type="button" class="career__add-toggle" onClick={() => onViewSource(contract.sourceId!)}>
            View original
          </button>
        </div>
      )}
    </div>
  );
}
